import React, { useMemo } from 'react';
import { View, StyleSheet } from 'react-native';
import { Table } from '../../DataTable/Table';
import { ColumnDefinition } from '../../../types/table';
import { getAllSectionProperties } from '../../../constants/sectionProperties';
import { debugLog } from '../../../constants/debug';
import { AssetNode, ProjectData, SchemaField } from '../../../types/project';
import { getDefaultUnit, getMeasureType } from '../../../utils/unitUtils';
import { processNodeData, findNodesByName, getNodesByParent, getAllNodesOfType } from '../../../utils/assetNodeUtils';
import type { TabComponentProps } from "../../../types/tabs"


export const SectionsTab: React.FC<TabComponentProps> = ({ selectedNode, projectData, menuAction }) => {
    // Generate column definitions from schema
     debugLog("SectionsTab rendered with:", { selectedNode, projectData, menuAction })
    const columns = useMemo(() => {
        const sectionSchema = projectData?.schemas?.Section;
        if (!sectionSchema) {
            debugLog('No Section schema found in projectData');
            return [];
        }

        debugLog('Generating columns with schema:', sectionSchema);

        const allProperties = getAllSectionProperties(sectionSchema);
        // Map the fields in the order defined in SECTION_PROPERTIES
        return allProperties.map(fieldKey => {
            // Find the schema entry that matches our property key
            const schemaEntries = Object.entries(sectionSchema) as [string, SchemaField][];
            const matchingSchema = schemaEntries.find(([key]) => key === fieldKey);

            if (!matchingSchema) {
                debugLog(`No schema found for field: ${fieldKey}`);
                return null;
            }

            const [_, field] = matchingSchema;

            const defaultUnit = getDefaultUnit(field);

            const column = {
                key: fieldKey,
                title: field.title || fieldKey,
                unit: defaultUnit,
                measure: getMeasureType(defaultUnit),
                sortable: true,
                filterable: true,
                align: 'center' as const,
                type: field.type
            } as ColumnDefinition;

            debugLog('Created column definition:', column);

            return column;
        }).filter((col): col is ColumnDefinition => col !== null);
    }, [projectData]);

    // Generate section data based on selection
    const sectionData = useMemo(() => {
        debugLog('Generating section data with:', {
            selectedNode,
            hasProjectData: !!projectData,
            columnsLength: columns.length
        });

        if (!projectData || !columns.length) return [];

        // Handle section selection
        if (selectedNode?.type === 'section') {
            debugLog('Processing section selection:', selectedNode.name);
            const sectionData = findNodesByName(projectData, selectedNode.name, 'section');
            debugLog('Found section data:', sectionData);

            // Ensure we only get the exact section we want
            const exactSectionData = sectionData.find(section =>
                section.name === selectedNode.name &&
                section.type === 'section'
            );

            debugLog('Filtered to exact section:', exactSectionData);

            // Return array with single section or empty array if not found
            return exactSectionData ? [processNodeData(exactSectionData, columns)] : [];
        }

        // Handle line selection or no selection
        if (!selectedNode || selectedNode.type === 'line') {
            debugLog('Processing all sections');
            const allSections = getAllNodesOfType(projectData, 'section');
            debugLog('Found all sections:', allSections);
            return allSections.map(section => processNodeData(section, columns));
        }

        return [];
    }, [selectedNode, projectData, columns]);

    return (
        <View style={styles.container}>
            <Table
                columns={columns}
                data={sectionData}
                title="Section Details"
                tableName="sections"
                pageSize={10}
                menuAction={menuAction}
            />
        </View>
    );
};

const styles = StyleSheet.create({
    container: {
        flex: 1
    }
});
